import type { PriceListUploadRecord, PriceListUploadResponse } from '@console/types'
import { useState } from 'react'
import { ApiError } from '../api.js'
import page from '../customer/CustomerPage.module.css'
import { SurfaceEmpty, SurfaceError, SurfaceLoading } from '../customer/SurfaceState.js'
import { formatInstant } from '../format.js'
import { useTenantPriceLists, useUploadTenantPriceList } from '../tenant-price-lists/queries.js'
import { Button, cx, Input, Stamp, Table, Toast } from '../ui/index.js'
import styles from './CustomerPriceListsPage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-039` — customer console: price list upload and versioning.
 *
 * Wire shape from `REQ-lighthouse-price-list-upload-and-versioning-shape`'s
 * own answer: two endpoints, `GET` for the full version history (newest
 * first), `POST` (multipart) that always names `supersedesVersionId` —
 * required, never simply absent, `''` meaning "no current version yet." The
 * confirm-replace step below reads the real current `active` entry from
 * that history and requires an explicit acknowledgement before the upload
 * fires, rather than a client-side flag with nothing behind it — the server
 * itself re-checks the same thing and answers `409 version_conflict` if the
 * admin's view was stale, so this is real conflict avoidance, not cosmetic.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `DES-001`, presentation only — the upload payload and the two queries are
 * untouched. What changed:
 *
 * - **The file input kept showing the old filename after a successful
 *   upload.** `setFile(null)` resets React's state, but `<input type="file">`
 *   is uncontrolled and holds its own value, so the screen read "a file is
 *   selected" while the form believed none was, and Upload stayed disabled
 *   with nothing saying why. Remounting the field on success is the fix.
 * - **`Upload` was the `secondary` variant, stretched full width.** The one
 *   irreversible action on the highest-leverage screen in this console,
 *   rendered in the low-emphasis variant and shaped like a disabled bar.
 * - **The replace gate looked like fine print** — see `.acknowledge`.
 * - **The history had no column headers**, and dropped `uploadedAt`,
 *   `uploadedByUserId`, `acceptedCount` and `refusedCount` entirely, though
 *   the wire carries all four on every record. Who replaced the price list and
 *   when are the two facts a versioned artifact exists to answer.
 * - **Two date formats on one screen**: the picker showed `01/10/2026` while
 *   the history showed `2026-10-01`. Both stay unambiguous now — see
 *   `formatEffectiveDate`.
 * ═══════════════════════════════════════════════════════════════════════════
 */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes.toFixed(0)} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/**
 * `effectiveDate` is a plain calendar date the server already fixed
 * (`"2026-09-01"`), not an instant — so this is rendering, not conversion, and
 * `format.ts`'s standing rule about never reading the browser's zone is not in
 * play. `timeZone: 'UTC'` is nonetheless pinned so the parse of a bare
 * `YYYY-MM-DD` cannot shift a day either side of midnight.
 *
 * `en-GB`'s "1 Oct 2026" is chosen over either numeric order for the reason
 * `DR-023` names: `01/10/2026` and `10/01/2026` are the same string to a
 * reader who does not know which convention produced it.
 */
const EFFECTIVE_DATE = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function formatEffectiveDate(value: string): string {
  const parsed = new Date(`${value}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? value : EFFECTIVE_DATE.format(parsed)
}

/** Enough refusals to see the pattern, without an unbounded list pushing the page away. */
const REFUSALS_SHOWN = 8

function UploadForm({ currentActive }: { currentActive: PriceListUploadRecord | null }) {
  const [file, setFile] = useState<File | null>(null)
  const [effectiveDate, setEffectiveDate] = useState('')
  const [supplier, setSupplier] = useState('')
  const [currency, setCurrency] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)
  const [lastResult, setLastResult] = useState<PriceListUploadResponse | null>(null)
  // Bumped on success so the uncontrolled file input is remounted empty.
  const [fileFieldKey, setFileFieldKey] = useState(0)
  const upload = useUploadTenantPriceList()

  const needsAcknowledgement = currentActive !== null
  const canSubmit =
    file !== null &&
    effectiveDate.trim() !== '' &&
    supplier.trim() !== '' &&
    currency.trim() !== '' &&
    (!needsAcknowledgement || acknowledged) &&
    !upload.isPending

  return (
    <form
      className={page['card']}
      onSubmit={(event) => {
        event.preventDefault()
        if (file === null) return
        upload.mutate(
          {
            file,
            effectiveDate,
            supersedesVersionId: currentActive?.id ?? null,
            supplier,
            currency,
          },
          {
            onSuccess: (result) => {
              setLastResult(result)
              setFile(null)
              setFileFieldKey((key) => key + 1)
              setEffectiveDate('')
              setSupplier('')
              setCurrency('')
              setAcknowledged(false)
            },
          },
        )
      }}
    >
      <h2 className={styles['cardTitle']}>Upload a new price list</h2>

      <Input
        key={fileFieldKey}
        label="Price list file"
        type="file"
        hint="The supplier's own file, as they sent it."
        onChange={(event) => {
          setFile(event.target.files?.[0] ?? null)
        }}
      />

      <div className={page['formRow']}>
        <Input
          label="Effective date"
          type="date"
          hint="The date these prices start applying."
          value={effectiveDate}
          onChange={(event) => {
            setEffectiveDate(event.target.value)
          }}
        />
        <Input
          label="Supplier"
          placeholder="e.g. Ducab"
          value={supplier}
          onChange={(event) => {
            setSupplier(event.target.value)
          }}
        />
        <Input
          label="Currency"
          placeholder="e.g. AED"
          hint="Three-letter code. Every price in the file is read as this."
          maxLength={3}
          value={currency}
          onChange={(event) => {
            setCurrency(event.target.value.toUpperCase())
          }}
        />
      </div>

      {needsAcknowledgement && (
        <label className={styles['acknowledge']}>
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(event) => {
              setAcknowledged(event.target.checked)
            }}
          />
          <span>
            I understand this replaces <strong>{currentActive.fileName}</strong>, which is pricing
            quotes right now.
          </span>
        </label>
      )}

      <div className={page['actions']}>
        <Button type="submit" variant="primary" disabled={!canSubmit} loading={upload.isPending}>
          {upload.isPending ? 'Uploading…' : 'Upload and replace'}
        </Button>
        {!canSubmit && !upload.isPending && (
          <span className={page['hint']}>
            {file === null
              ? 'Choose a file to continue.'
              : needsAcknowledgement && !acknowledged
                ? 'Confirm the replacement above to continue.'
                : 'Fill in the date, supplier and currency to continue.'}
          </span>
        )}
      </div>

      {upload.isError && (
        <p role="alert" className={page['error']}>
          {upload.error instanceof ApiError
            ? upload.error.message
            : 'That upload could not be sent.'}
        </p>
      )}

      {lastResult !== null && !upload.isError && (
        <div className={styles['result']}>
          <Toast variant={lastResult.upload.refusedCount > 0 ? 'info' : 'ok'}>
            {lastResult.upload.fileName} is now the active price list —{' '}
            {lastResult.upload.acceptedCount} row
            {lastResult.upload.acceptedCount === 1 ? '' : 's'} accepted
            {lastResult.upload.refusedCount > 0 &&
              `, ${lastResult.upload.refusedCount.toFixed(0)} row${lastResult.upload.refusedCount === 1 ? '' : 's'} refused`}
            .
          </Toast>
          {lastResult.refused !== undefined && lastResult.refused.length > 0 && (
            <>
              <ul className={styles['refusedList']}>
                {lastResult.refused.slice(0, REFUSALS_SHOWN).map((row) => (
                  <li key={row.rowId}>
                    Row {row.rowId}: {row.reason}
                  </li>
                ))}
              </ul>
              {lastResult.refused.length > REFUSALS_SHOWN && (
                <p className={styles['refusedMore']}>
                  …and {(lastResult.refused.length - REFUSALS_SHOWN).toFixed(0)} more refused rows.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </form>
  )
}

export function CustomerPriceListsPage() {
  const history = useTenantPriceLists()

  if (history.isError) {
    return (
      <main className={page['page']}>
        <h1 className={page['title']}>Price lists</h1>
        <SurfaceError>The price list history could not be loaded.</SurfaceError>
      </main>
    )
  }

  if (history.isPending) {
    return (
      <main className={page['page']}>
        <h1 className={page['title']}>Price lists</h1>
        <SurfaceLoading rows={4} />
      </main>
    )
  }

  const current = history.data.find((record) => record.status === 'active') ?? null

  return (
    <main className={page['page']}>
      <h1 className={page['title']}>Price lists</h1>
      <p className={page['lede']}>
        The active price list is what every quote is priced from. Uploading a new one replaces it.
      </p>

      {current !== null ? (
        <p className={styles['current']}>
          <span className={styles['currentLabel']}>Pricing quotes right now</span>
          <strong className={styles['fileName']}>{current.fileName}</strong> — effective{' '}
          {formatEffectiveDate(current.effectiveDate)} · {current.supplier} · {current.currency}
        </p>
      ) : (
        <p className={cx(styles['current'], styles['currentNone'])}>
          <span className={styles['currentLabel']}>Pricing quotes right now</span>
          Nothing. Until a price list is uploaded, this account cannot price a quote.
        </p>
      )}

      <UploadForm currentActive={current} />

      <h2 className={page['sectionTitle']}>Version history</h2>
      {history.data.length === 0 ? (
        <SurfaceEmpty>Nothing has been uploaded yet.</SurfaceEmpty>
      ) : (
        <Table
          rows={history.data}
          rowKey={(record) => record.id}
          columns={[
            {
              key: 'file',
              header: 'File',
              render: (record) => <span className={styles['fileName']}>{record.fileName}</span>,
            },
            {
              key: 'effective',
              header: 'Effective',
              render: (record) => formatEffectiveDate(record.effectiveDate),
            },
            {
              key: 'supplier',
              header: 'Supplier',
              render: (record) => `${record.supplier} · ${record.currency}`,
            },
            {
              key: 'rows',
              header: 'Rows',
              render: (record) =>
                record.refusedCount > 0
                  ? `${record.acceptedCount.toFixed(0)} in, ${record.refusedCount.toFixed(0)} refused`
                  : `${record.acceptedCount.toFixed(0)} in`,
            },
            { key: 'size', header: 'Size', render: (record) => formatBytes(record.sizeBytes) },
            {
              key: 'uploaded',
              header: 'Uploaded',
              render: (record) => formatInstant(record.uploadedAt),
            },
            {
              key: 'status',
              header: 'Status',
              render: (record) => (
                <span className={styles['status']}>
                  {record.status === 'active' ? (
                    <>
                      <Stamp variant="ok" label="In use" /> Active
                    </>
                  ) : (
                    <>
                      <Stamp variant="open" label="Replaced" />{' '}
                      <span className={page['muted']}>Superseded</span>
                    </>
                  )}
                </span>
              ),
            },
          ]}
        />
      )}
    </main>
  )
}
